import React from 'react';
import { CastMember, ReviewFinding } from '../../../api/structs';
import { castForFinding } from './cast/castFindings';
import { castMemberImageUrl } from './cast/castMedia';

/**
 * One judged criterion per row — a PASS/FAIL/UNCERTAIN pill, the criterion,
 * and its evidence. Shared between the pipeline-run `CheckpointVerdictPanel`
 * and the clip-side `ReviewerPanel` so both review surfaces read identically.
 *
 * A failed check about a character also shows that character's CAST picture:
 * "is this the same person?" is a question a reference frame answers and a
 * sentence does not. Callers that pass no cast render exactly as before.
 */

export const FINDING_STYLES: Record<string, string> = {
  PASS: 'border-emerald-400/40 text-emerald-300',
  FAIL: 'border-amber-400/50 text-amber-300',
  UNCERTAIN: 'border-white/25 text-zinc-300',
  UNREVIEWED: 'border-dashed border-white/25 text-zinc-400',
};

interface ReviewFindingsListProps {
  findings: ReviewFinding[];
  /** The run's cast, for the reference picture beside a failed cast check. */
  cast?: CastMember[];
}

const ReviewFindingsList: React.FC<ReviewFindingsListProps> = ({ findings, cast }) => {
  if (findings.length === 0) return null;

  return (
    <ul className="space-y-1">
      {findings.map((finding, index) => {
        const level = (finding.verdict || '').toUpperCase();
        const members = castForFinding(finding, cast);
        return (
          <li key={`${finding.criterion}-${index}`} className="space-y-0.5">
            <div className="flex items-start gap-1.5">
              <span
                data-testid="review-finding-pill"
                className={`shrink-0 border px-1 py-0.5 text-[9px] uppercase tracking-wide ${
                  FINDING_STYLES[level] || FINDING_STYLES.UNCERTAIN
                }`}
              >
                {level || 'UNCERTAIN'}
              </span>
              <span className="text-[11px] text-zinc-200">{finding.criterion}</span>
              {/* An advisory FAIL never failed the frame; without the tag it
                  reads exactly like one that did. */}
              {finding.advisory && (
                <span
                  data-testid="review-finding-advisory"
                  className="shrink-0 border border-white/15 px-1 text-[9px] uppercase tracking-wide text-zinc-400"
                >
                  advisory
                </span>
              )}
            </div>
            {finding.evidence && (
              <p className="pl-[3.25rem] text-[10px] text-zinc-400">{finding.evidence}</p>
            )}
            {members.length > 0 && (
              <div
                data-testid="review-finding-cast"
                className="flex flex-wrap items-center gap-2 pl-[3.25rem]"
              >
                {members.map((member) => (
                  <span key={member.name} className="flex items-center gap-1">
                    <img
                      src={castMemberImageUrl(member)}
                      alt={member.name}
                      title={`${member.name} as the cast has them`}
                      className="h-8 w-8 border border-white/15 object-cover"
                    />
                    <span className="font-mono text-[9px] text-zinc-400">{member.name}</span>
                  </span>
                ))}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default ReviewFindingsList;
